export type VehicleStatus =
  | 'critical_event'
  | 'no_signal'
  | 'offline'
  | 'stopped'
  | 'in_route';

export interface VehiclePosition {
  lat: number;
  lng: number;
  heading?: number;
  speed?: number;
  timestamp: string;
}

// `[0,0]` es el centinela de "sin posición en vivo todavía": `vehicleFromDto`
// cae a 0,0 cuando la proyección del vehículo no trae `coords` (equipo recién
// instalado o aún sin primera señal). Ningún vehículo real reporta Null Island
// (mar abierto), así que sirve para distinguir "esperando primera señal" de una
// ubicación real — y evitar que el mapa vuele al océano, donde Esri sirve el
// tile gris "Map data not yet available".
export function hasLivePosition(position: VehiclePosition): boolean {
  return !(position.lat === 0 && position.lng === 0);
}

// FE-23 — vencimientos del vehículo. Espejo PROVISIONAL del diseño de BE-58
// (`docs/backend/Epicas_Backend.md`): `Vehicle` gana dos objetos HERMANOS, no un
// `maintenance` — tienen escritores distintos (documentos: `PATCH .../documents`;
// aceite: `POST .../oil-change`), y `maintenance` era la forma del mock, que
// reusar invitaba a arrastrar. Cuando el contrato esté en `openapi.yaml`, lo que
// cambia es el mapper (`lib/api/mappers.ts`); estos tipos y todo lo que los lee
// no deberían.
//
// Todo es opcional a propósito: "no se sabe" NO es "vigente" ni "vencido", y
// cada superficie tiene que poder decir "Sin datos" (ver `utils/maintenance.ts`).

/**
 * Las tres fechas que trae el papel. `YYYY-MM-DD` tal cual: es el DÍA impreso,
 * no un instante — parsearlas como `new Date(str)` las lee en UTC y el vencimiento
 * cae un día antes en Bogotá (UTC-5).
 */
export interface VehicleDocuments {
  soatExpiresOn?: string;
  tecnomecanicaExpiresOn?: string;
  insuranceExpiresOn?: string;
}

/**
 * De dónde sale el consumo de kilómetros del aceite. El odómetro es del EQUIPO,
 * no del vehículo (`VehicleOdometer`): puede reiniciarse o bajar, y entonces el
 * consumo ya no se puede medir — nunca se recorta a 0, que se leería "recién
 * cambiado".
 */
export type OilKmBasis = 'MEASURED' | 'UNAVAILABLE' | 'RESET';

/**
 * Último cambio de aceite registrado y el intervalo que el usuario tecleó ENTONCES
 * ("vuelve a tocar dentro de…"). Es relativo a la lectura estampada; no hay meta
 * absoluta. Los dos intervalos son independientes: con uno solo, ese manda.
 */
export interface VehicleOilChange {
  /** `YYYY-MM-DD` del último cambio. */
  lastOn?: string;
  /** Odómetro del equipo estampado en ese momento. */
  lastKm?: number;
  intervalKm?: number;
  intervalMonths?: number;
  /** `null` cuando `kmBasis` no es `MEASURED`. Jamás 0 como sustituto. */
  kmSinceLastChange?: number | null;
  kmBasis?: OilKmBasis;
}

export type CameraLabel = 'FRONT' | 'CABIN' | 'REAR' | 'SIDE_LEFT' | 'SIDE_RIGHT' | 'OTHER';

export interface VehicleCamera {
  id: string;
  label: CameraLabel;
  name?: string | null;
  enabled: boolean;
}

/**
 * Odómetro ACUMULADO que reporta el equipo GPS, en kilómetros y tal cual lo
 * entrega el proveedor (BE-50). `undefined` = todavía no se sabe, NO cero.
 *
 * ⚠️ Es del EQUIPO, no del vehículo: al reemplazar el dispositivo el número
 * vuelve a empezar y puede BAJAR.
 * ⚠️ NO es "kilómetros recorridos" de ningún periodo. Restar dos lecturas no da
 * lo recorrido: solo se guarda la última, y entre dos el equipo pudo estar mudo.
 * ⚠️ `measuredAt` puede ser ANTERIOR a `position.timestamp` — el proveedor no
 * manda la lectura en todas las tramas. Preséntalo SIEMPRE con su fecha.
 */
export interface VehicleOdometer {
  km: number;
  measuredAt: string;
}

export interface Vehicle {
  id: string;
  plate: string;
  model: string;
  brand: string;
  year: number;
  /** Clase comercial (NHR, NPR, turbo…): decide qué tarifas aplican (FE-32 · ADR-030). */
  vehicleClass?: string;
  status: VehicleStatus;
  position: VehiclePosition;
  driverId?: string;
  driverName?: string;
  subUnitId: string;
  subUnitName: string;
  tenantId: string;
  deviceId: string;
  activeAlerts: number;
  lastSeen: string;
  /** FE-23 / BE-58. `undefined` = el contrato todavía no lo entrega (o el vehículo no tiene fechas). */
  documents?: VehicleDocuments;
  oilChange?: VehicleOilChange;
  /** Odómetro del equipo con su instante de medición. Ver VehicleOdometer. */
  odometer?: VehicleOdometer;
  // FE-14: solo vienen poblados en `GET /vehicles/{id}` (el detalle) — el
  // listado los omite a propósito para evitar un N+1 (openapi.yaml,
  // `Vehicle.cameras`). No asumas que están presentes fuera del detalle.
  cameras?: VehicleCamera[];
  hasLiveVideo?: boolean;
}
